import { FC, MouseEvent, useState } from 'react';
import {
  DevHubBox,
  DevHubButton,
  DevHubColorProps,
  DevHubDynamicIcon,
  DevHubList,
  DevHubListItem,
  DevHubListItemText,
  DevHubPopover,
} from '@devxdiscover/devhub-ui';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck';
import { Feature } from '@devxdiscover/devhub-common';
import useGoogleAnalytics from '../../util/useGoogleAnalytics';
import { GA4_EVENTS } from '../../util/analyticsEvents';

type DocItemLink = {
  url: string;
  title: string;
};

type DocItem = {
  title: string;
  links: DocItemLink[];
};

interface MarketplaceDocsButtonProps {
  color?: DevHubColorProps;
}

/**
 * Custom button component for marketplace template docs
 * @param props - MarketplaceDocsButtonProps
 * @constructor
 */
const MarketplaceDocsButton: FC<MarketplaceDocsButtonProps> = (
  props: MarketplaceDocsButtonProps,
) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { sendEvent } = useGoogleAnalytics();

  const config = useApi(configApiRef);

  const baseUrl = config.getString('app.baseUrl');

  const docItems: DocItem[] = [
    {
      title: 'DevHub Templates',
      links: [
        {
          title: 'Template docs',
          url: `${baseUrl}/docs/default/Component/a8d77616f4a55dc3fd0af51e1a2122488eecde73`,
        },
      ],
    },
    {
      title: 'Installed Actions',
      links: [
        {
          title: 'Installed Actions',
          url: `${baseUrl}/marketplace/actions`,
        },
      ],
    },
  ];

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (url: string | URL | undefined) => {
    handleClose();
    window.open(url, '_blank');
  };

  const open = Boolean(anchorEl);
  const id = open ? 'doc-popover' : undefined;

  return (
    <>
      <ConfigFeatureCheck feature={Feature.NEW_MARKETPLACE}>
        <DevHubButton
          onClick={() => {
            sendEvent({
              action: GA4_EVENTS.NEW_MARKETPLACE_CTA_CLICKED,
              subject: 'New Marketplace CTA Clicked',
            });
            window.location.href = '/newmarketplace';
          }}
          buttonColor="pinkPurple"
          variant="contained"
        >
          Try the new experience ✨
        </DevHubButton>
      </ConfigFeatureCheck>
      <DevHubButton
        textColor={props.color || 'primary'}
        onClick={handleClick}
        aria-describedby={id}
      >
        <DevHubDynamicIcon iconName="LibraryBooks" /> Docs
      </DevHubButton>
      <DevHubPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <DevHubBox p={1}>
          <DevHubList>
            {docItems.map(item => (
              <DevHubListItem
                key={item.title}
                button
                onClick={() => handleOptionClick(item.links[0].url)}
              >
                <DevHubListItemText primary={item.title} />
              </DevHubListItem>
            ))}
          </DevHubList>
          <DevHubBox display="flex" justifyContent="flex-end">
            <DevHubButton onClick={handleClose} color="primary" variant="text">
              Close
            </DevHubButton>
          </DevHubBox>
        </DevHubBox>
      </DevHubPopover>
    </>
  );
};

export default MarketplaceDocsButton;
